import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_SERIES } from '../constants/totalAnalytics.constants';
import type { PeriodSummary, TrendPoint } from '../types/totalAnalytics.types';

type CampaignTrendsSectionProps = {
  dateRangeLabel: string;
  periodSummary: PeriodSummary;
  trendsData: TrendPoint[];
  isLoading?: boolean;
};

export function CampaignTrendsSection({
  dateRangeLabel,
  periodSummary,
  trendsData,
  isLoading,
}: CampaignTrendsSectionProps) {
  const subtitle =
    dateRangeLabel
      ? `Showing all campaign data · ${dateRangeLabel}`
      : periodSummary.name && periodSummary.name !== '—'
        ? `Showing all campaign data · ${periodSummary.name}`
        : 'Showing all campaign data';

  return (
    <section className="rounded-2xl border border-[var(--dash-line)] bg-white p-4 shadow-sm sm:p-5">
      <div className="text-left">
        <h3 className="m-0 text-[15px] font-bold text-[var(--dash-ink)]">
          Campaign Trends Over Time
        </h3>
        <p className="mt-0.5 mb-0 text-[13px] text-[var(--dash-muted)]">
          {subtitle}
        </p>
      </div>

      <div className="mt-3 rounded-2xl border border-[var(--dash-line)] bg-white p-3">
        <p className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
          {periodSummary.name}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
          {CHART_SERIES.map((series) => (
            <div
              key={series.key}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: series.color }}
                />
                <span className="text-[var(--dash-muted)]">{series.label}</span>
              </div>
              <span className="font-semibold text-[var(--dash-ink)]">
                {periodSummary[series.key]}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 h-[280px] w-full min-w-0 sm:h-[320px]">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center text-sm text-[var(--dash-muted)]">
            Loading chart data...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={trendsData}
              margin={{ top: 12, right: 16, left: -10, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94A3B8', fontSize: 11 }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94A3B8', fontSize: 11 }}
                domain={[0, 'auto']}
                allowDecimals={false}
                tickCount={5}
                width={36}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: 12,
                  fontSize: 12,
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => (
                  <span className="text-[11px] font-medium text-[var(--dash-muted)]">
                    {value}
                  </span>
                )}
              />
              {CHART_SERIES.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={series.strokeWidth ?? 2}
                  dot={{ r: 3, fill: series.color, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
